import { loadStripe } from "@stripe/stripe-js";
import {
  PaymentElement,
  Elements,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import Button from "../../form/Button";
import { toast } from "react-toastify";
import { useMutation } from "@tanstack/react-query";
import { decodeId, decrypt } from "../../../helpers";
import { useSelector } from "react-redux";

const CheckoutForm = ({ callbackUrl, getResponse = () => {} }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { lang } = useSelector((state) => state?.langProvider);

  const { mutate: handleSubmit, isPending } = useMutation({
    mutationFn: async () => {
      if (!stripe || !elements) {
        throw new Error("Stripe is not loaded.");
      }

      const { error: submitError } = await elements.submit();
      if (submitError) {
        throw new Error(submitError.message);
      }

      const { error } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url:
            callbackUrl || window.location.origin + `/${lang}/thank-you`,
        },
      });

      if (error) {
        throw new Error(error.message);
      }

      return { success: true };
    },
    onSuccess: () => {
      getResponse({ status: 200 });
      toast.success("Payment successful! Redirecting...");
    },
    onError: (error) => {
      toast.error(error.message || "Payment failed.");
    },
  });

  return (
    <div className="space-y-3 flex flex-col">
      <PaymentElement />
      <Button
        buttonFunction={handleSubmit}
        disabled={!stripe || !elements || isPending}
        buttonClasses={"ml-auto"}
        buttonLabel={isPending ? "Processing..." : "Pay"}
        {...(isPending && {
          loading: true,
          buttonIconPosition: "left",
          isDisable: true,
        })}
      />
    </div>
  );
};

const StripePayment = ({
  clientSecret,
  intentId,
  merchantId,
  callbackUrl,
  getResponse = () => {},
}) => {
  const client_secret_key = decrypt(
    clientSecret,
    process.env.REACT_APP_STRIPE_DECODE_KEY
  );
  const merchant_secret_key = decrypt(
    merchantId,
    process.env.REACT_APP_STRIPE_DECODE_KEY
  );

  const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY, {
    stripeAccount: merchant_secret_key,
  });

  const options = {
    clientSecret: client_secret_key,
    appearance: {
      theme: "flat",
    },
    wallets: {
      link: "never",
    },
  };

  return (
    <div className="w-full">
      {client_secret_key && (
        <Elements stripe={stripePromise} options={options}>
          <CheckoutForm callbackUrl={callbackUrl} response={getResponse} />
        </Elements>
      )}
    </div>
  );
};

export default StripePayment;
